/**
 * Collection / PLP behaviour.
 *
 * Everything here is an upgrade on markup that already works without it: the
 * two filter surfaces are plain GET forms, sort is a set of radios inside one
 * of them, "Load more" is a link to page 2, and each facet is a <details>. With
 * this script the same interactions become Section Rendering API fetches that
 * swap only the regions that changed.
 *
 * Regions are swapped rather than the whole section being replaced, so the
 * drawer stays open while a filter is applied — including its applied chips
 * and "View N products" count — and the rail keeps the groups the shopper
 * expanded. The drawer form must carry `data-filter-form` or a checkbox
 * change never reaches applyForm.
 */

/**
 * @typedef {Object} SwapTarget
 * @property {string} selector - Region to replace, relative to the section root
 * @property {boolean} [optional] - Skip silently when the region is absent on either side
 */

/** @type {SwapTarget[]} */
const SWAP_TARGETS = [
  { selector: '.collection-toolbar', optional: true },
  { selector: '.collection-mobile-bar', optional: true },
  { selector: '.collection-applied-mobile', optional: true },
  { selector: '.collection-rail', optional: true },
  { selector: '.collection-results' },
  { selector: '.collection-drawer', optional: true },
];

const MOBILE_QUERY = '(max-width: 991px)';

/**
 * Open floating surfaces in the toolbar — the sort menu and, in the horizontal
 * filter layout, each facet chip's panel. They dismiss on Escape and on a click
 * outside themselves; the rail and the drawer are in-flow and do not.
 */
const DROPDOWN_SELECTOR = '.collection-sort[open], .collection-facet--bar[open]';

/** Everything the drawer's focus trap is allowed to land on. */
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

if (!customElements.get('collection-page')) {
  class CollectionPage extends HTMLElement {
    abortController = undefined;
    priceDebounce = undefined;
    drawerHideTimeout = undefined;

    connectedCallback() {
      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onPopState = this.onPopState.bind(this);
      this.onKeydown = this.onKeydown.bind(this);

      this.addEventListener('change', this.onChange);
      this.addEventListener('input', this.onInput);
      this.addEventListener('submit', this.onSubmit);
      this.addEventListener('click', this.onClick);
      document.addEventListener('click', this.onDocumentClick);
      document.addEventListener('keydown', this.onKeydown);
      window.addEventListener('popstate', this.onPopState);

      this.setupInfiniteScroll();
      this.setupStickyToolbar();
      this.setupInterrupterHeights();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      clearTimeout(this.priceDebounce);
      clearTimeout(this.drawerHideTimeout);
      this.observer?.disconnect();
      this.toolbarResizeObserver?.disconnect();
      this.interrupterResizeObserver?.disconnect();
      cancelAnimationFrame(this.interrupterFrame);
      document.removeEventListener('click', this.onDocumentClick);
      document.removeEventListener('keydown', this.onKeydown);
      window.removeEventListener('popstate', this.onPopState);
      this.unlockScroll();
    }

    get sectionId() {
      return this.dataset.sectionId;
    }

    get results() {
      return this.querySelector('.collection-results');
    }

    get drawer() {
      return this.querySelector('[data-filter-drawer]');
    }

    /* Events
       -------------------------------------------------------------------- */

    onChange(event) {
      const form = event.target.closest('[data-filter-form]');
      if (!form) {
        return;
      }

      // Price inputs debounce through onInput instead.
      if (event.target.matches('.collection-price__input')) {
        return;
      }

      this.applyForm(form);
    }

    onInput(event) {
      if (!event.target.matches('.collection-price__input')) {
        return;
      }

      const form = event.target.closest('[data-filter-form]');
      if (!form) {
        return;
      }

      clearTimeout(this.priceDebounce);
      this.priceDebounce = setTimeout(() => this.applyForm(form), 500);
    }

    onSubmit(event) {
      const form = event.target.closest('[data-filter-form], .collection-drawer__panel');
      if (!form) {
        return;
      }

      event.preventDefault();
      this.applyForm(form);
      this.closeDrawer();
    }

    onClick(event) {
      const railToggle = event.target.closest('[data-rail-toggle]');
      if (railToggle) {
        this.toggleRail(railToggle);
        return;
      }

      if (event.target.closest('[data-filter-drawer-open]')) {
        this.openDrawer();
        return;
      }

      if (event.target.closest('[data-filter-drawer-close]')) {
        event.preventDefault();
        this.closeDrawer();
        return;
      }

      const loadMore = event.target.closest('[data-load-more]');
      if (loadMore) {
        event.preventDefault();
        this.loadMore(loadMore);
        return;
      }

      const removeChip = event.target.closest('.collection-applied__chip, .collection-applied__clear');
      if (removeChip) {
        event.preventDefault();
        this.navigate(removeChip.href);
      }
    }

    onDocumentClick(event) {
      // A click on a closed chip's own summary is not caught here: the native
      // toggle runs after the event, so the panel it is about to open is not
      // yet [open]. Any other open dropdown is.
      this.closeDropdowns(event.target);
    }

    onKeydown(event) {
      if (event.key === 'Tab') {
        this.trapFocus(event);
        return;
      }

      if (event.key !== 'Escape') {
        return;
      }

      this.closeDropdowns();

      if (this.drawer?.hasAttribute('data-open')) {
        this.closeDrawer();
      }
    }

    /**
     * @param {EventTarget} [keep] - Leave open the dropdown containing this node
     */
    closeDropdowns(keep) {
      for (const dropdown of this.querySelectorAll(DROPDOWN_SELECTOR)) {
        if (keep && dropdown.contains(keep)) {
          continue;
        }
        dropdown.removeAttribute('open');
      }
    }

    /**
     * Keeps Tab inside the drawer while it is open. `aria-modal="true"` promises
     * the rest of the page is unreachable; without this it is only a claim.
     * @param {KeyboardEvent} event
     */
    trapFocus(event) {
      const drawer = this.drawer;
      if (!drawer?.hasAttribute('data-open')) {
        return;
      }

      const focusable = [...drawer.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null);

      if (!focusable.length) {
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
        return;
      }

      if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    onPopState() {
      this.render(window.location.href, { push: false });
    }

    /* Filtering
       -------------------------------------------------------------------- */

    /**
     * Turns a filter form into a collection URL and renders it.
     * @param {HTMLFormElement} form
     */
    applyForm(form) {
      const params = new URLSearchParams();

      for (const [key, value] of new FormData(form).entries()) {
        if (value === '') {
          continue;
        }
        // The drawer always submits a sort, because its radios always show one.
        // Dropping the collection's own default keeps it out of the URL without
        // making the control lie about what is selected.
        if (key === 'sort_by' && value === this.dataset.defaultSort) {
          continue;
        }
        params.append(key, value);
      }

      const base = form.getAttribute('action') || this.dataset.collectionUrl;
      const query = params.toString();
      this.render(query ? `${base}?${query}` : base);
    }

    /**
     * @param {string} url
     */
    navigate(url) {
      this.render(url);
    }

    /**
     * Fetches a collection URL as a section render and swaps the regions that
     * changed.
     * @param {string} url
     * @param {{push?: boolean}} [options]
     * @returns {Promise<void>}
     */
    async render(url, options = {}) {
      const { push = true } = options;

      this.abortController?.abort();
      this.abortController = new AbortController();
      this.setBusy(true);

      try {
        const target = new URL(url, window.location.origin);
        target.searchParams.set('section_id', this.sectionId);

        const response = await fetch(target.toString(), { signal: this.abortController.signal });
        if (!response.ok) {
          throw new Error(`Collection render failed: ${response.status}`);
        }

        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const source = html.querySelector('collection-page');
        if (!source) {
          throw new Error('Collection render returned no section');
        }

        const openFacets = this.openFacetKeys();
        const activeId = this.contains(document.activeElement) ? document.activeElement.id : '';
        this.swapRegions(source);
        this.restoreOpenFacets(openFacets);
        this.announceRender({ reason: 'filter' });

        if (activeId) {
          this.querySelector(`[id="${CSS.escape(activeId)}"]`)?.focus({ preventScroll: true });
        }

        if (push) {
          const pushed = new URL(url, window.location.origin);
          pushed.searchParams.delete('section_id');
          window.history.pushState({}, '', pushed.toString());
        }
      } catch (error) {
        if (error.name === 'AbortError') {
          return;
        }
        console.error(error);
        // Fall back to a full navigation so the shopper still gets their result.
        window.location.href = url;
      } finally {
        this.setBusy(false);
      }
    }

    /**
     * @param {Element} source - The freshly rendered <collection-page>
     */
    swapRegions(source) {
      for (const { selector, optional } of SWAP_TARGETS) {
        const next = source.querySelector(selector);
        const current = this.querySelector(selector);

        if (!next || !current) {
          if (!optional) {
            console.warn(`Collection render is missing ${selector}`);
          }
          if (current && !next) {
            current.remove();
          }
          continue;
        }

        const wasOpen = current.hasAttribute('data-open');
        current.replaceWith(next);
        if (wasOpen) {
          next.setAttribute('data-open', '');
        }
        if (wasOpen) {
          next.removeAttribute('hidden');
        }
      }

      this.observeStickyToolbar();
      this.observeInterrupterHeights();
    }

    /**
     * Every filter surface is rebuilt on each fetch, so the groups the shopper
     * expanded have to be reapplied afterwards. `data-facet` names the surface
     * as well as the filter, which is what keeps the three surfaces from
     * reopening each other's groups.
     * @returns {string[]}
     */
    openFacetKeys() {
      return [...this.querySelectorAll('.collection-facet[data-facet][open]')].map((facet) => facet.dataset.facet);
    }

    /**
     * @param {string[]} keys - Facets to leave open; every other one is closed
     */
    restoreOpenFacets(keys) {
      for (const facet of this.querySelectorAll('.collection-facet[data-facet]')) {
        facet.toggleAttribute('open', keys.includes(facet.dataset.facet));
      }
    }

    /**
     * @param {boolean} busy
     */
    setBusy(busy) {
      this.results?.setAttribute('aria-busy', String(busy));
    }

    /* Sticky toolbar
       -------------------------------------------------------------------- */

    setupStickyToolbar() {
      this.syncToolbarOffset = this.syncToolbarOffset.bind(this);
      this.toolbarResizeObserver = new ResizeObserver(this.syncToolbarOffset);
      this.observeStickyToolbar();
    }

    observeStickyToolbar() {
      this.toolbarResizeObserver?.disconnect();

      if (!this.hasAttribute('data-sticky-toolbar')) {
        this.style.removeProperty('--collection-toolbar-offset');
        return;
      }

      const toolbar = this.querySelector('.collection-toolbar');
      const mobileBar = this.querySelector('.collection-mobile-bar');

      if (toolbar) {
        this.toolbarResizeObserver.observe(toolbar);
      }

      if (mobileBar) {
        this.toolbarResizeObserver.observe(mobileBar);
      }

      this.syncToolbarOffset();
    }

    syncToolbarOffset() {
      if (!this.hasAttribute('data-sticky-toolbar')) {
        this.style.removeProperty('--collection-toolbar-offset');
        return;
      }

      const toolbar = this.querySelector('.collection-toolbar');
      const mobileBar = this.querySelector('.collection-mobile-bar');
      let height = 0;

      if (toolbar?.offsetParent) {
        height = toolbar.offsetHeight;
      } else if (mobileBar?.offsetParent) {
        height = mobileBar.offsetHeight;
      }

      this.style.setProperty('--collection-toolbar-offset', `${height}px`);
    }

    /* Interrupter height
       -------------------------------------------------------------------- */

    setupInterrupterHeights() {
      this.syncInterrupterHeights = this.syncInterrupterHeights.bind(this);
      this.interrupterResizeObserver = new ResizeObserver(this.syncInterrupterHeights);
      this.observeInterrupterHeights();
    }

    observeInterrupterHeights() {
      this.interrupterResizeObserver?.disconnect();

      const grid = this.querySelector('.collection-grid');
      if (!grid) {
        return;
      }

      this.interrupterResizeObserver.observe(grid);

      for (const card of grid.querySelectorAll('.collection-card')) {
        if (card.offsetParent) {
          this.interrupterResizeObserver.observe(card);
        }
      }

      this.syncInterrupterHeights();
    }

    /**
     * Spotlight and shoppable tiles should stop where the product CTA starts,
     * not at the Figma pixel heights — column count and title wrap change the
     * card. Measure a card in the same row (the double often sits later than
     * the first card) using the info block, so row stretch cannot feed back.
     */
    syncInterrupterHeights() {
      if (this.interrupterFrame) {
        return;
      }

      this.interrupterFrame = requestAnimationFrame(() => {
        this.interrupterFrame = 0;
        this.applyInterrupterHeights();
      });
    }

    measureCardToCta(card) {
      if (!card) {
        return 0;
      }

      const info = card.querySelector('.collection-card__info');
      const cta = card.querySelector('.collection-card__cta');
      if (!info || !cta) {
        return 0;
      }

      const gap = parseFloat(getComputedStyle(card).rowGap || getComputedStyle(card).gap) || 0;
      const ctaMargin = parseFloat(getComputedStyle(cta).marginBlockStart) || 0;

      return Math.round(info.getBoundingClientRect().bottom - card.getBoundingClientRect().top + gap + ctaMargin);
    }

    applyInterrupterHeights() {
      const grid = this.querySelector('.collection-grid');
      if (!grid) {
        return;
      }

      const tiles = [
        ...grid.querySelectorAll('.collection-spotlight:not(.collection-spotlight--fill-row), .collection-shoppable:not(.collection-shoppable--fill-row)'),
      ].filter((tile) => tile.offsetParent);
      const cards = [...grid.querySelectorAll('.collection-card')].filter((card) => card.offsetParent);

      if (!tiles.length) {
        grid.style.removeProperty('--collection-interrupter-height');
        return;
      }

      const fallback = this.measureCardToCta(cards[0]);
      if (fallback) {
        grid.style.setProperty('--collection-interrupter-height', `${fallback}px`);
      }

      for (const tile of tiles) {
        const top = tile.getBoundingClientRect().top;
        const neighbor = cards.find((card) => Math.abs(card.getBoundingClientRect().top - top) <= 2);
        const height = this.measureCardToCta(neighbor) || fallback;

        if (height) {
          tile.style.setProperty('--collection-interrupter-height', `${height}px`);
        }
      }
    }

    /* Rail
       -------------------------------------------------------------------- */

    /**
     * @param {HTMLElement} toggle
     */
    toggleRail(toggle) {
      const rail = document.getElementById(toggle.getAttribute('aria-controls'));
      if (!rail) {
        return;
      }

      const expanded = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', String(!expanded));
      rail.hidden = expanded;
      rail.closest('.collection-body__inner')?.classList.toggle('collection-body__inner--full', expanded);
    }

    /* Drawer
       -------------------------------------------------------------------- */

    openDrawer() {
      const drawer = this.drawer;
      if (!drawer) {
        return;
      }

      drawer.hidden = false;
      // Flush layout so the panel paints off-canvas before the transform
      // transitions in. A reflow rather than requestAnimationFrame: rAF never
      // fires in a backgrounded tab, which would leave the drawer marked open
      // but still sitting at translateX(100%) — that is, invisible.
      drawer.getBoundingClientRect();
      drawer.setAttribute('data-open', '');
      this.lockScroll();
      drawer.querySelector('.collection-drawer__close')?.focus();
    }

    closeDrawer() {
      const drawer = this.drawer;
      if (!drawer?.hasAttribute('data-open')) {
        return;
      }

      drawer.removeAttribute('data-open');
      this.unlockScroll();
      this.querySelector('[data-filter-drawer-open]')?.focus();

      // transitionend on its own would leave the drawer stuck interactive when
      // the transition never runs — reduced motion, or a backgrounded tab — so
      // the timeout is what actually guarantees it goes away.
      const hide = () => {
        clearTimeout(this.drawerHideTimeout);
        drawer.hidden = true;
      };

      drawer.querySelector('.collection-drawer__panel')?.addEventListener('transitionend', hide, { once: true });
      this.drawerHideTimeout = setTimeout(hide, 400);
    }

    lockScroll() {
      document.body.style.overflow = 'hidden';
    }

    unlockScroll() {
      document.body.style.overflow = '';
    }

    /* Pagination
       -------------------------------------------------------------------- */

    /**
     * @param {HTMLAnchorElement} button
     */
    async loadMore(button) {
      if (button.getAttribute('aria-busy') === 'true') {
        return;
      }

      button.setAttribute('aria-busy', 'true');
      const label = button.textContent;
      button.textContent = button.dataset.loadingLabel ?? label;

      try {
        const target = new URL(button.href, window.location.origin);
        target.searchParams.set('section_id', this.sectionId);

        const response = await fetch(target.toString());
        if (!response.ok) {
          throw new Error(`Load more failed: ${response.status}`);
        }

        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const grid = this.querySelector('[data-collection-grid]');
        const nextItems = [...html.querySelectorAll('[data-collection-grid] > *')];

        for (const item of nextItems) {
          grid?.append(item);
        }

        const nextMore = html.querySelector('[data-collection-more]');
        const currentMore = this.querySelector('[data-collection-more]');

        if (nextMore && currentMore) {
          currentMore.replaceWith(nextMore);
        } else {
          currentMore?.remove();
        }

        // The next page renders its "Showing x of y" counting every page up to
        // it, which is what is now on screen.
        const nextCounts = html.querySelectorAll('[data-product-count]');
        for (const [index, count] of this.querySelectorAll('[data-product-count]').entries()) {
          if (nextCounts[index]) count.textContent = nextCounts[index].textContent;
        }

        const pushed = new URL(button.href, window.location.origin);
        pushed.searchParams.delete('section_id');
        window.history.replaceState({}, '', pushed.toString());
        this.setupInfiniteScroll();
        this.announceRender({ reason: 'more', items: nextItems });
      } catch (error) {
        console.error(error);
        button.removeAttribute('aria-busy');
        button.textContent = label;
      }
    }

    /**
     * Tells listeners (assets/component-collection-motion.js) that the grid
     * changed: `filter` re-rendered it, `more` appended `items` to it.
     * @param {{reason: 'filter' | 'more', items?: Element[]}} detail
     */
    announceRender(detail) {
      this.dispatchEvent(new CustomEvent('collection:render', { bubbles: true, detail }));
    }

    setupInfiniteScroll() {
      this.observer?.disconnect();
      if (this.dataset.pagination !== 'infinite') {
        return;
      }

      const button = this.querySelector('[data-load-more]');
      if (!button) {
        return;
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              this.loadMore(button);
            }
          }
        },
        { rootMargin: '400px' },
      );

      this.observer.observe(button);
    }
  }

  customElements.define('collection-page', CollectionPage);
}

/**
 * The flash sale countdown.
 *
 * Liquid renders the opening values, so this only keeps them true — it never
 * paints the first frame. It ticks every half minute because the frame shows
 * days, hours and minutes and nothing finer, and it removes the whole band on
 * expiry so a finished sale cannot sit at 00/00/00.
 */
if (!customElements.get('sale-countdown')) {
  class SaleCountdown extends HTMLElement {
    timer = undefined;

    connectedCallback() {
      this.endsAt = Date.parse(this.dataset.end);
      if (Number.isNaN(this.endsAt)) {
        return;
      }

      this.units = {
        days: this.querySelector('[data-countdown="days"]'),
        hours: this.querySelector('[data-countdown="hours"]'),
        minutes: this.querySelector('[data-countdown="minutes"]'),
      };

      this.tick();
      this.timer = setInterval(() => this.tick(), 30000);
    }

    disconnectedCallback() {
      clearInterval(this.timer);
    }

    tick() {
      const remaining = Math.floor((this.endsAt - Date.now()) / 1000);

      if (remaining <= 0) {
        this.closest('.collection-sale')?.remove();
        return;
      }

      const pad = (value) => String(value).padStart(2, '0');
      this.units.days.textContent = pad(Math.floor(remaining / 86400));
      this.units.hours.textContent = pad(Math.floor((remaining % 86400) / 3600));
      this.units.minutes.textContent = pad(Math.floor((remaining % 3600) / 60));
    }
  }

  customElements.define('sale-countdown', SaleCountdown);
}

/**
 * The drawer only exists below 992px. Closing it on the way up to desktop stops
 * the body scroll lock outliving the surface that set it.
 */
window.matchMedia(MOBILE_QUERY).addEventListener('change', (event) => {
  if (event.matches) {
    return;
  }

  for (const page of document.querySelectorAll('collection-page')) {
    page.closeDrawer();
  }
});
